import { useEffect, useState } from 'react'

import { useTranslation } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { MoonIcon, SunIcon } from './Icons'

const STORAGE_KEY = 'sss-theme'

type Theme = 'light' | 'dark'

function apply(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark')
  document.documentElement.style.colorScheme = theme
}

/**
 * Spec 12 · "Toggled by the Sun/Moon control in the header."
 *
 * Shows the theme you'd switch *to*, which is what the icon pair in
 * the spec sheet implies — sun while dark, moon while light. Choice
 * persists in localStorage; the system preference is only the initial
 * guess.
 *
 * The switch itself is a circular wipe (à la trydawn.us): the new
 * theme floods out of the toggle via the View Transitions API. On
 * browsers without it, or under reduced motion, it falls back to the
 * instant flip.
 */
function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation()
  const [theme, setTheme] = useState<Theme>('dark')

  useEffect(() => {
    // v2 is dark-primary — light is the stored opt-in inversion.
    const stored = window.localStorage.getItem(STORAGE_KEY) as Theme | null
    const initial: Theme = stored ?? 'dark'
    setTheme(initial)
    apply(initial)
  }, [])

  const toggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    const commit = () => {
      setTheme(next)
      apply(next)
      window.localStorage.setItem(STORAGE_KEY, next)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const startViewTransition = (document as any).startViewTransition?.bind(document)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!startViewTransition || reduced) {
      commit()
      return
    }

    // The wipe grows from the toggle's centre to the farthest corner.
    const rect = event.currentTarget.getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.top + rect.height / 2
    const radius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    )

    startViewTransition(commit).ready.then(() => {
      document.documentElement.animate(
        {
          clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`],
        },
        {
          duration: 550,
          easing: 'cubic-bezier(0.2, 0, 0, 1)',
          pseudoElement: '::view-transition-new(root)',
        }
      )
    })
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={theme === 'dark' ? t('layout.theme_light') : t('layout.theme_dark')}
      className={cn(
        'flex cursor-pointer items-center justify-center text-ink transition-colors duration-[120ms]',
        'hover:text-signal-deep',
        'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_5px_var(--sss-ring)]',
        className
      )}
    >
      {theme === 'dark' ? <SunIcon size={20} /> : <MoonIcon size={20} />}
    </button>
  )
}

export { ThemeToggle }
